import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Paperclip, Smile, Mic } from "lucide-react";

/* ─── Reuse the same 3D bevel tokens as AuthSwitch ──────── */
const G = {
  blue: "#4285F4",
  blueLit: "#6EA1FF",
  blueDeep: "#2A56C6",
  red: "#EA4335",
  redDeep: "#B23023",
  white: "#FFFFFF",
  bg: "#F8F9FA",
  text: "#202124",
  soft: "#5F6368",
  faint: "#B7BFCB",
  border: "#E8EAED",
};

const SUPPORT_ENDPOINT =
  "https://europe-west3-ebuosystem.cloudfunctions.net/CustomerSupportAgent";

type ChatMessage = { role: "user" | "agent"; text: string };

/* ─── Small icon button used in the input row ───────────── */
const IconBtn: React.FC<{ onClick?: () => void; disabled?: boolean; children: React.ReactNode }> = ({
  onClick,
  disabled,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      width: 30,
      height: 30,
      flexShrink: 0,
      border: "none",
      background: "transparent",
      color: disabled ? G.faint : G.soft,
      cursor: disabled ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50%",
      transition: "background .15s, color .15s",
    }}
    onMouseEnter={e => !disabled && (e.currentTarget.style.background = G.border)}
    onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
  >
    {children}
  </button>
);

const CustomerSupport: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: "agent", text: "Hi! How can we help you today? 👋" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, isOpen]);

  const canSend = input.trim().length > 0 && !loading;

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    setError("");
    setMessages(prev => [...prev, { role: "user", text: trimmed }]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(SUPPORT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: trimmed,
          history: messages.map(m => ({ role: m.role, text: m.text })),
        }),
      });

      if (!res.ok) throw new Error(`Request failed (${res.status})`);

      const data = await res.json();
      // Adjust this to match your actual Cloud Function response shape.
      const reply = data?.reply ?? data?.message ?? "Got it — someone will follow up shortly.";

      setMessages(prev => [...prev, { role: "agent", text: reply }]);
    } catch (e: any) {
      setError("Couldn't reach support — please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && canSend) handleSend();
  };

  const handleAttachClick = () => fileInputRef.current?.click();

  return (
    <div style={{ position: "fixed", right: 22, bottom: 110, zIndex: 1000, fontFamily: "'Nunito', sans-serif" }}>
      {/* ── Floating chat card ─────────────────────────── */}
      {isOpen && (
        <div
          style={{
            width: 340,
            height: "min(78vh, 640px)",
            maxHeight: "calc(100vh - 150px)",
            marginBottom: 14,
            background: G.white,
            borderRadius: 26,
            boxShadow: `0 0 0 4px ${G.white}, 0 0 0 8px ${G.border}, 0 14px 0 ${G.faint}, 0 34px 60px -20px rgba(32,33,36,.4)`,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            animation: "supportPop .32s cubic-bezier(.2,.8,.25,1) both",
          }}
        >
          <style>{`
            @keyframes supportPop { from{opacity:0; transform:translateY(14px) scale(.97);} to{opacity:1; transform:none;} }
            @keyframes spin { to { transform: rotate(360deg); } }
          `}</style>

          {/* Header */}
          <div
            style={{
              padding: "16px 18px",
              background: `linear-gradient(168deg, ${G.blueLit} 0%, ${G.blue} 60%, ${G.blueDeep} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ color: G.white, fontWeight: 800, fontSize: 15, fontFamily: "'Baloo 2', sans-serif" }}>
              Support
            </span>
            <div onClick={() => setIsOpen(false)} style={{ cursor: "pointer", color: G.white, display: "flex" }}>
              <X size={18} strokeWidth={2.6} />
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: "16px 14px",
              background: G.bg,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {messages.map((m, i) => (
              <div
                key={i}
                style={{
                  alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                  maxWidth: "78%",
                  padding: "9px 13px",
                  borderRadius: 16,
                  fontSize: 13,
                  fontWeight: 600,
                  lineHeight: 1.35,
                  color: m.role === "user" ? G.white : G.text,
                  background: m.role === "user" ? G.blue : G.white,
                  boxShadow: m.role === "user" ? `0 3px 0 ${G.blueDeep}` : `inset 0 0 0 2px ${G.border}`,
                }}
              >
                {m.text}
              </div>
            ))}
            {loading && (
              <div style={{ alignSelf: "flex-start", color: G.soft, display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700 }}>
                <Loader2 size={13} style={{ animation: "spin 0.8s linear infinite" }} />
                typing…
              </div>
            )}
          </div>

          {error && (
            <div style={{ padding: "8px 14px", fontSize: 11.5, fontWeight: 700, color: G.redDeep, background: "#FDECEA" }}>
              {error}
            </div>
          )}

          {/* ── Input row — attach / emoji / gif / mic + pill field + send ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "10px 10px 10px 6px",
              background: G.white,
              borderTop: `2px solid ${G.border}`,
            }}
          >
            <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={() => {}} />

            <IconBtn onClick={handleAttachClick} disabled={loading}>
              <Paperclip size={17} strokeWidth={2.3} />
            </IconBtn>
            <IconBtn disabled={loading}>
              <Smile size={17} strokeWidth={2.3} />
            </IconBtn>
            <IconBtn disabled={loading}>
              <span style={{ fontSize: 10, fontWeight: 800, fontFamily: "'Baloo 2', sans-serif" }}>GIF</span>
            </IconBtn>

            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Message…"
              disabled={loading}
              style={{
                flex: 1,
                minWidth: 0,
                border: "none",
                outline: "none",
                background: G.bg,
                borderRadius: 999,
                padding: "9px 14px",
                fontSize: 13.5,
                fontWeight: 700,
                color: G.text,
                boxShadow: `inset 0 0 0 2px ${G.border}`,
                margin: "0 4px",
              }}
            />

            {canSend ? (
              <button
                onClick={handleSend}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  border: "none",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  background: `linear-gradient(168deg, ${G.blueLit} 0%, ${G.blue} 60%, ${G.blueDeep} 100%)`,
                  boxShadow: `0 0 0 2px ${G.white}, 0 0 0 4px ${G.blueDeep}, 0 4px 0 ${G.blueDeep}`,
                  transition: "transform .15s",
                }}
                onMouseDown={e => (e.currentTarget.style.transform = "translateY(2px)")}
                onMouseUp={e => (e.currentTarget.style.transform = "none")}
              >
                {loading ? (
                  <Loader2 size={15} color={G.white} style={{ animation: "spin 0.8s linear infinite" }} />
                ) : (
                  <Send size={15} color={G.white} strokeWidth={2.6} />
                )}
              </button>
            ) : (
              <IconBtn disabled>
                <Mic size={18} strokeWidth={2.3} />
              </IconBtn>
            )}
          </div>
        </div>
      )}

      {/* ── Launcher bubble ─────────────────────────────── */}
      <button
        onClick={() => setIsOpen(o => !o)}
        style={{
          width: 58,
          height: 58,
          borderRadius: "50%",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(168deg, ${G.blueLit} 0%, ${G.blue} 60%, ${G.blueDeep} 100%)`,
          boxShadow: `0 0 0 4px ${G.white}, 0 0 0 7px ${G.blueDeep}, 0 9px 0 ${G.blueDeep}, 0 20px 30px -10px rgba(32,33,36,.35)`,
          marginLeft: "auto",
          transition: "transform .16s cubic-bezier(.2,.9,.3,1.4)",
        }}
        onMouseDown={e => (e.currentTarget.style.transform = "translateY(3px)")}
        onMouseUp={e => (e.currentTarget.style.transform = "none")}
      >
        {isOpen ? <X size={24} color={G.white} strokeWidth={2.6} /> : <MessageCircle size={24} color={G.white} strokeWidth={2.6} />}
      </button>
    </div>
  );
};

export default CustomerSupport;